import { Injectable, signal } from '@angular/core';

import { createLogger } from '../../../utils/logger.util';
import { RINGTONE_TYPE } from '../constants';
import { CallRingtoneType } from '../types/call';

@Injectable({ providedIn: 'root' })
export class CallAudioService {
  private readonly logger = createLogger('Call - CallAudioService');
  private audioMap = new Map<CallRingtoneType, HTMLAudioElement>();
  private current: CallRingtoneType | null = null;

  // Element dùng riêng để mở khoá autoplay (first user gesture) và phát âm báo
  // kết thúc cuộc gọi. Được giữ xuyên suốt, KHÔNG bị giải phóng cùng ringtone
  // nên âm "end-call" không bị cắt khi cuộc gọi kết thúc.
  private endCallAudio: HTMLAudioElement | null = null;

  private _soundEnabled = signal(false);
  readonly soundEnabled = this._soundEnabled.asReadonly();

  markUserEnabled() {
    this._soundEnabled.set(true);
  }

  private channel = new BroadcastChannel('call-audio');

  private debugLog(event: string, payload: Record<string, unknown> = {}): void {
    // const ts = Date.now();
    // console.log(`${this.debugPrefix} ${event}`, {
    //   ts,
    //   iso: new Date(ts).toISOString(),
    //   tabId: this.tabId,
    //   ...payload,
    // });
  }

  constructor() {
    this.channel.onmessage = (e) => {
      if (e.data === 'STOP_ALL_AUDIO') {
        this.debugLog('audio.stop-by-channel', {
          current: this.current,
        });
        this.stop(false);
      }
    };
  }

  // Tạo sẵn ringtone khi bắt đầu có cuộc gọi (gọi đi hoặc gọi đến).
  // Idempotent: gọi nhiều lần vẫn an toàn.
  initAudio(): void {
    if (this.audioMap.size > 0) return;

    this.audioMap.set(
      RINGTONE_TYPE.CALLEE_INCOMING,
      this.createAudio('/assets/audio/callee_incoming-ringtone-1.mp3', true),
    );
    this.audioMap.set(
      RINGTONE_TYPE.OUTGOING,
      this.createAudio('/assets/audio/outgoing-ringtone-1.mp3', true),
    );
    this.audioMap.set(
      RINGTONE_TYPE.RINGING,
      this.createAudio('/assets/audio/ringing-ringtone-1.mp3', true),
    );
  }

  // Giải phóng ringtone khi cuộc gọi kết thúc. Không đụng tới endCallAudio.
  disposeAudio(): void {
    this.audioMap.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
      audio.src = '';
      audio.load();
    });
    this.audioMap.clear();

    if (this.current !== RINGTONE_TYPE.END_CALL) {
      this.current = null;
    }
    this.debugLog('audio.dispose');
  }

  private getEndCallAudio(): HTMLAudioElement {
    if (!this.endCallAudio) {
      this.endCallAudio = this.createAudio('/assets/audio/end-call-1.wav', false);
    }
    return this.endCallAudio;
  }

  private createAudio(src: string, loop = false): HTMLAudioElement {
    const audio = new Audio(src);
    audio.loop = loop;
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';
    return audio;
  }

  async enableSound(): Promise<void> {
    if (this.soundEnabled()) return;

    try {
      // unlock bằng audio thật thay vì silent wav
      const audio = this.getEndCallAudio();

      audio.volume = 0;

      try {
        await audio.play();
      } catch (err) {
        this.logger.warn('Audio unlock failed:', err);
      }

      audio.pause();
      audio.currentTime = 0;
      audio.volume = 1;

      this._soundEnabled.set(true);
    } catch (err) {
      this.logger.warn('Enable sound failed:', err);
    }
  }

  play(type: CallRingtoneType): void {
    if (!this.soundEnabled()) return;
    if (this.current === type) return;

    this.debugLog('audio.play-request', {
      type,
      current: this.current,
    });
    this.channel.postMessage('STOP_ALL_AUDIO');
    this.stop(false);

    // End-call là âm báo ngắn, dùng element riêng để độc lập với init/dispose.
    const audio =
      type === RINGTONE_TYPE.END_CALL ? this.getEndCallAudio() : this.audioMap.get(type);
    if (!audio) return;

    audio.currentTime = 0;

    audio
      .play()
      .then(() => {
        this.current = type;
        this.debugLog('audio.play-success', {
          type,
        });
      })
      .catch((err) => {
        this.logger.warn(`Cannot play ${type}:`, err);
      });
  }

  stop(broadcast = true): void {
    // if (!this.current) return;

    this.audioMap.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
    });

    if (this.endCallAudio) {
      this.endCallAudio.pause();
      this.endCallAudio.currentTime = 0;
    }

    this.current = null;
    this.debugLog('audio.stop', {
      broadcast,
    });

    if (broadcast) {
      this.channel.postMessage('STOP_ALL_AUDIO');
    }
  }
}
